import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  AccessToken,
  CartItemChangeQuantityRequest,
  CartItemRequest,
  EventPhoto,
  OrderState,
  OrderWithItems,
  PaymentMethod,
  ShoppingCartState,
  ShoppingCartWithItems,
  SwishPaymentData,
} from 'common-models';
import { Configuration } from '../../Configuration';

export const eventShopApiSlice = createApi({
  reducerPath: 'shopApi',
  tagTypes: ['cart', 'carts', 'order', 'orders', 'orderphotos', 'swishpayment'],
  baseQuery: fetchBaseQuery({
    baseUrl: Configuration.ApiUrl,
    prepareHeaders(headers) {
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // query for getting shopping cart
    getShoppingCart: builder.query<ShoppingCartWithItems, [string, ShoppingCartState]>({
      query: ([visitorId, state]) => `/eventsshop/carts/${visitorId}/${state}`,
      providesTags: (result) => [{ type: 'cart', id: 'main' }],
    }),

    getAllOrders: builder.query<Array<OrderWithItems>, AccessToken>({
      query: (accessToken) => {
        return {
          url: `/eventsshop/orders/`,
          method: 'get',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        };
      },
      providesTags: [{ type: 'orders', id: 'all-orders' }],
    }),

    getOrder: builder.query<OrderWithItems, string>({
      query: (orderId) => `/eventsshop/orders/${orderId}`,
      providesTags: (result) => [{ type: 'order', id: result?.id }],
    }),

    // eventsshop/orders/:orderId/photos
    getPhotosForOrder: builder.query<Array<EventPhoto>, string>({
      query: (orderId) => `/eventsshop/orders/${orderId}/photos`,
      providesTags: (result, error, input) => [{ type: 'orderphotos', id: input }],
    }),

    getOrderState: builder.query<OrderState, string>({
      query: (orderId) => `/eventsshop/orders/${orderId}/state`,
    }),

    getOrdersByVisitorId: builder.query<Array<OrderWithItems>, string>({
      query: (visitorId) => `/eventsshop/visitors/${visitorId}/orders`,
      providesTags: (result) => [{ type: 'orders', id: 'by-visitor' }],
    }),

    getOrderFromShoppingCart: builder.mutation<OrderWithItems, string>({
      query: (cartId) => {
        return {
          url: `/eventsshop/carts/${cartId}/order/swish`,
          method: 'put',
        };
      },
      invalidatesTags: ['cart', 'carts', 'order', 'orders'],
    }),

    deleteShoppingCart: builder.mutation<any, [string, AccessToken]>({
      query: ([identifier, accessToken]) => {
        return {
          url: `/admin/eventsshop/carts/${identifier}`,
          method: 'delete',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        };
      },
      invalidatesTags: ['cart', 'carts'],
    }),

    initiatePaymentForOrder: builder.mutation<string, [string, PaymentMethod]>({
      query: ([orderId, paymentMethod]) => {
        return {
          url: `/eventsshop/orders/${orderId}/paymentmethod/${paymentMethod}`,
          method: 'put',
        };
      },
      invalidatesTags: ['cart'],
    }),

    reopenShoppingCart: builder.mutation<any, { orderId: string; cartId: string }>({
      query: ({ orderId, cartId }) => {
        return {
          url: `/eventsshop/orders/${orderId}/${cartId}/states/open`,
          method: 'put',
        };
      },
      invalidatesTags: ['cart', 'order', 'orders'],
    }),

    startSwishPayment: builder.mutation<any, { orderId: string }>({
      query: (input) => {
        return {
          url: `/eventsshop/orders/${input.orderId}/swishpayment`,
          method: 'put',
        };
      },
      invalidatesTags: ['order'],
    }),

    getSwishPaymentForOrder: builder.query<SwishPaymentData, string>({
      query: (orderId) => `/eventsshop/orders/${orderId}/swishpayment`,
      providesTags: (result) => [{ type: 'swishpayment', id: result?.paymentId }],
    }),

    getAllShoppingCarts: builder.query<ShoppingCartWithItems[], AccessToken>({
      query: (accessToken) => ({
        url: '/admin/eventsshop/carts',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }),
      providesTags: [{ type: 'carts', id: 'all-carts' }],
    }),

    // mutation for changing the quantity of a product in the cart
    changeProductQuantity: builder.mutation<void, { visitorId: string; request: CartItemRequest<CartItemChangeQuantityRequest> }>(
      {
        query: (arg) => {
          const { visitorId, request } = arg;

          console.info(`request: ${JSON.stringify(request)}`);

          const url = `/eventsshop/carts/${visitorId}`;
          console.info(`url: ${url}`);
          return {
            url: url,
            method: 'post',
            body: request,
          };
        },
        invalidatesTags: [
          { type: 'cart', id: 'main' },
          { type: 'carts', id: 'all-carts' },
        ],
      }
    ),

    sendCompletedOrderMail: builder.mutation<void, { orderId: string; emailAddress: string }>({
      query: (arg) => {
        const { orderId, emailAddress } = arg;
        const url = `/eventsshop/orders/${orderId}/mail/${emailAddress}/sv`;
        console.info(`url: ${url}`);
        return {
          url: url,
          method: 'post',
        };
      },
    }),
  }),
});

export const {
  useGetShoppingCartQuery,
  useLazyGetShoppingCartQuery,
  useGetAllOrdersQuery,
  useGetOrderQuery,
  useGetPhotosForOrderQuery,
  useLazyGetOrderQuery,
  useGetOrderStateQuery,
  useReopenShoppingCartMutation,
  useGetOrdersByVisitorIdQuery,
  useGetOrderFromShoppingCartMutation,
  useInitiatePaymentForOrderMutation,
  useGetSwishPaymentForOrderQuery,
  useStartSwishPaymentMutation,
  useGetAllShoppingCartsQuery,
  useDeleteShoppingCartMutation,
  useChangeProductQuantityMutation,
  useSendCompletedOrderMailMutation,
} = eventShopApiSlice;
